'use client';

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import { CarpetItem } from '@/lib/carpetMeasurements';
import { CurtainItem } from '@/lib/curtainMeasurements';

interface SeatMeasurement {
  seatNumber: number;
  width: number;
}

interface ServiceMeasurementData {
  numberOfSeats?: number;
  seatMeasurements?: SeatMeasurement[];
  // Multi-size curtain shape (preferred). Legacy single-size fields below
  // are still readable so AI verification on pre-feature bookings works.
  curtainItems?: Array<{ label?: string; width: number; length: number; quantity: number }>;
  curtainWidth?: number;
  curtainLength?: number;
  numberOfPanels?: number;
  // Multi-size carpet shape (preferred). Legacy single-size fields below
  // are still readable so AI verification on pre-feature bookings works.
  carpetItems?: Array<{ label?: string; width: number; length: number; quantity: number }>;
  carpetWidth?: number;
  carpetLength?: number;
  numberOfCarpets?: number;
  quantity?: number;
  measurementUnit?: 'feet' | 'meters';
  images?: string[];
}

interface AIVerificationResult {
  success: boolean;
  itemType: string;
  measurements: any;
  confidence?: number;
  description?: string;
}

interface MeasurementVerificationStepProps {
  serviceType: 'sofa' | 'curtain' | 'carpet' | 'upholstery';
  serviceName: string;
  userMeasurements: ServiceMeasurementData;
  onImagesChange?: (images: string[]) => void;
  onMeasurementChange?: (measurements: ServiceMeasurementData) => void;
  onVerificationComplete: (verified: boolean, aiResults?: AIVerificationResult[], aiVerificationData?: any) => void;
  // Override the itemType sent to /api/ai-measure while keeping the
  // serviceType-driven comparison logic. Used by loose-cushion services
  // (DB type='upholstery' so the per-piece pricing path works, but the
  // AI prompt should count cushions, not generic "items"). Valid values
  // map to keys in the prompts table in /api/ai-measure/route.ts:
  // 'sofa' | 'curtain' | 'carpet' | 'cushion' | 'ottoman' | 'chair'.
  aiItemTypeOverride?: string;
  // When true, the AI is the SOLE source of truth for measurements (the
  // customer's manual inputs are hidden in this mode). After analysis we
  // overwrite the measurements with the AI's detected quantity/dimensions
  // so the price reflects the photo — and if the AI analysed successfully
  // but couldn't read a usable measurement for the booked service (e.g.
  // the photo is ottomans on a "Sofa" booking), we surface a mismatch
  // warning instead of silently pricing ₹0 / showing a false "verified".
  // When false (manual / verify mode), AI only flags discrepancies and we
  // keep the legacy "raise the value only if AI is higher" behaviour.
  aiSourceOfTruth?: boolean;
  // When rendered INLINE inside Step 1 (the "AI verification" measurement
  // mode) rather than as its own standalone step, suppress the big
  // "Step 3 / Verify Measurements" page header — the surrounding mode
  // selector already provides the context. Standalone usage (if any)
  // keeps the full header.
  embedded?: boolean;
}

type SlotImage = { dataUrl: string; mimeType: string };

const MAX_ITEM_SLOTS = 10;

function parseStoredImages(imgs: string[]): SlotImage[] {
  return (imgs || []).map((img) => {
    if (img.startsWith('data:')) {
      const match = img.match(/data:([^;]+);/);
      return { dataUrl: img, mimeType: match ? match[1] : 'image/jpeg' };
    }
    return { dataUrl: img, mimeType: 'image/jpeg' };
  });
}

function emptyCarpetRow(): CarpetItem {
  return { label: '', width: 0, length: 0, quantity: 1 };
}

function emptyCurtainRow(): CurtainItem {
  return { label: '', width: 0, length: 0, quantity: 1 };
}

function getRawCarpetRows(meas: ServiceMeasurementData): CarpetItem[] {
  if (meas.carpetItems && meas.carpetItems.length > 0) return meas.carpetItems;
  return [emptyCarpetRow()];
}

function getRawCurtainRows(meas: ServiceMeasurementData): CurtainItem[] {
  if (meas.curtainItems && meas.curtainItems.length > 0) return meas.curtainItems;
  return [emptyCurtainRow()];
}

function allItemSlotsMeasured(meas: ServiceMeasurementData, type: 'carpet' | 'curtain'): boolean {
  const rows = type === 'carpet' ? getRawCarpetRows(meas) : getRawCurtainRows(meas);
  return rows.length > 0 && rows.every((r) => r.width > 0 && r.length > 0);
}

function clearedAiMeasurements(
  type: string,
  images: string[],
  prev?: ServiceMeasurementData,
  opts?: { multiItem?: boolean; clearSlotOnly?: number },
): ServiceMeasurementData {
  const base: ServiceMeasurementData = {
    ...prev,
    images,
    measurementUnit: prev?.measurementUnit || 'feet',
  };

  if (opts?.multiItem && (type === 'carpet' || type === 'curtain') && opts.clearSlotOnly != null) {
    const slot = opts.clearSlotOnly;
    if (type === 'carpet') {
      const items = [...getRawCarpetRows(base)];
      while (items.length <= slot) items.push(emptyCarpetRow());
      items[slot] = { ...items[slot], width: 0, length: 0, quantity: items[slot].quantity || 1 };
      const valid = items.filter((r) => r.width > 0 && r.length > 0);
      return {
        ...base,
        carpetItems: items,
        carpetWidth: valid[0]?.width || 0,
        carpetLength: valid[0]?.length || 0,
        numberOfCarpets: valid.length,
      };
    }
    const items = [...getRawCurtainRows(base)];
    while (items.length <= slot) items.push(emptyCurtainRow());
    items[slot] = { ...items[slot], width: 0, length: 0, quantity: items[slot].quantity || 1 };
    const valid = items.filter((r) => r.width > 0 && r.length > 0);
    return {
      ...base,
      curtainItems: items,
      curtainWidth: valid[0]?.width || 0,
      curtainLength: valid[0]?.length || 0,
      numberOfPanels: valid.reduce((s, r) => s + (r.quantity || 0), 0),
    };
  }

  switch (type) {
    case 'sofa':
      return { ...base, numberOfSeats: 0, seatMeasurements: [] };
    case 'upholstery':
      return { ...base, quantity: 0 };
    case 'carpet':
      return {
        ...base,
        carpetItems: [emptyCarpetRow()],
        carpetWidth: 0,
        carpetLength: 0,
        numberOfCarpets: 0,
      };
    case 'curtain':
      return {
        ...base,
        curtainItems: [emptyCurtainRow()],
        curtainWidth: 0,
        curtainLength: 0,
        numberOfPanels: 0,
      };
    default:
      return base;
  }
}

const MeasurementVerificationStep = ({
  serviceType,
  serviceName,
  userMeasurements,
  onImagesChange,
  onMeasurementChange,
  onVerificationComplete,
  aiItemTypeOverride,
  aiSourceOfTruth = false,
  embedded = false,
}: MeasurementVerificationStepProps) => {
  // The AI prompt key — defaults to serviceType. Parent passes 'cushion'
  // for loose-cushion services so the model counts cushions instead of
  // the generic upholstery (no-prompt → falls back to sofa seat counter).
  const aiItemType = aiItemTypeOverride || serviceType;
  const multiItemMode =
    aiSourceOfTruth && (serviceType === 'carpet' || serviceType === 'curtain');
  const itemNoun = serviceType === 'curtain' ? 'Curtain' : 'Carpet';

  const [activeSlot, setActiveSlot] = useState(0);
  const [slotImages, setSlotImages] = useState<Record<number, SlotImage[]>>(() => ({
    0: parseStoredImages(userMeasurements.images || []),
  }));

  const currentImages = useMemo(
    () => slotImages[multiItemMode ? activeSlot : 0] ?? [],
    [slotImages, activeSlot, multiItemMode],
  );

  const allSlotImageUrls = useMemo(
    () =>
      multiItemMode
        ? Object.keys(slotImages)
            .sort((a, b) => Number(a) - Number(b))
            .flatMap((k) => (slotImages[Number(k)] ?? []).map((img) => img.dataUrl))
        : (slotImages[0] ?? []).map((img) => img.dataUrl),
    [slotImages, multiItemMode],
  );

  const setCurrentImages = useCallback(
    (next: SlotImage[]) => {
      const key = multiItemMode ? activeSlot : 0;
      setSlotImages((prev) => ({ ...prev, [key]: next }));
    },
    [activeSlot, multiItemMode],
  );
  const [uploading, setUploading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationResults, setVerificationResults] = useState<AIVerificationResult[]>([]);
  const [comparisons, setComparisons] = useState<Array<{
    imageIndex: number;
    matches: boolean;
    discrepancies: Array<{ field: string; userValue: string; aiValue: string; difference: string }>;
    confidence: number;
  }>>([]);
  const [aggregatedMeasurements, setAggregatedMeasurements] = useState<any>({});
  const [allVerified, setAllVerified] = useState(false);
  // AI analysed successfully but couldn't read a usable measurement for the
  // booked service (likely the photo shows a different item). Source-of-truth
  // mode only — drives the "doesn't match this service" warning.
  const [serviceMismatch, setServiceMismatch] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The parent passes a fresh inline `onImagesChange` on every render. Keeping
  // it in the sync effect's dep array made the effect fire every render →
  // setState in the parent → re-render → new callback → effect again →
  // "Maximum update depth exceeded". Hold the latest callback in a ref so the
  // sync effect depends ONLY on the images actually changing.
  const onImagesChangeRef = useRef(onImagesChange);
  useEffect(() => {
    onImagesChangeRef.current = onImagesChange;
  });

  // Same ref pattern for onVerificationComplete — used by the image-reset
  // effect to re-lock the parent's Continue when photos change, without
  // re-running the effect every render (the parent passes a fresh callback).
  const onVerificationCompleteRef = useRef(onVerificationComplete);
  useEffect(() => {
    onVerificationCompleteRef.current = onVerificationComplete;
  });

  useEffect(() => {
    onImagesChangeRef.current?.(allSlotImageUrls);
  }, [allSlotImageUrls]);

  const currentImagesFingerprint = currentImages.map((img) => img.dataUrl).join('|');

  // Switching carpet/curtain slots — don't show the previous slot's AI card.
  useEffect(() => {
    if (!multiItemMode) return;
    setVerificationResults([]);
    setComparisons([]);
    setAggregatedMeasurements({});
    setServiceMismatch(false);
  }, [activeSlot, multiItemMode]);

  // When photos for the active item change, any prior AI result is stale.
  // In source-of-truth mode also clear applied measurements so the sidebar
  // price drops until the customer re-verifies.
  const didMountResetRef = useRef(false);
  useEffect(() => {
    if (!didMountResetRef.current) {
      didMountResetRef.current = true;
      return;
    }
    setVerificationResults([]);
    setComparisons([]);
    setAggregatedMeasurements({});
    setAllVerified(false);
    setServiceMismatch(false);

    if (aiSourceOfTruth && onMeasurementChange) {
      onMeasurementChange(
        clearedAiMeasurements(serviceType, allSlotImageUrls, userMeasurements, {
          multiItem: multiItemMode,
          clearSlotOnly: multiItemMode ? activeSlot : undefined,
        }),
      );
    }

    if (aiSourceOfTruth) {
      onVerificationCompleteRef.current(false, [], {
        verified: false,
        matches: false,
        aiMeasurements: {},
        userMeasurements,
        discrepancies: [],
        confidence: 0,
        verificationResults: [],
        comparisons: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImagesFingerprint]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const inputEl = e.target;
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const extToMime: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
    };

    const resolveMime = (file: File): string | null => {
      if (file.type && validTypes.includes(file.type)) return file.type;
      const ext = file.name.toLowerCase().split('.').pop();
      if (ext && extToMime[ext]) return extToMime[ext];
      return null;
    };

    const accepted: Array<{ file: File; mimeType: string }> = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const mimeType = resolveMime(file);
      if (!mimeType) {
        alert(`Image ${file.name} has an unsupported format. Please use JPEG, PNG, GIF, or WebP.`);
        continue;
      }
      if (file.size > 1 * 1024 * 1024) {
        alert(`Image ${file.name} is too large. Maximum size is 1 MB per image.`);
        continue;
      }
      accepted.push({ file, mimeType });
    }

    // Always reset the input so the same file can be re-selected after an error.
    inputEl.value = '';

    if (accepted.length === 0) {
      setUploading(false);
      return;
    }

    setUploading(true);
    const newImages: SlotImage[] = [];
    let pending = accepted.length;

    const finishReader = () => {
      pending -= 1;
      if (pending > 0) return;
      if (newImages.length > 0) {
        const key = multiItemMode ? activeSlot : 0;
        setSlotImages((prev) => ({
          ...prev,
          [key]: [...(prev[key] ?? []), ...newImages],
        }));
      }
      setUploading(false);
    };

    try {
      for (const { file, mimeType } of accepted) {
        const reader = new FileReader();
        reader.onerror = () => {
          alert(`Failed to read ${file.name}. Please try again.`);
          finishReader();
        };
        reader.onloadend = () => {
          const base64String = reader.result;
          if (typeof base64String !== 'string' || !base64String.startsWith('data:')) {
            alert(`Failed to read ${file.name}. Please try again.`);
            finishReader();
            return;
          }
          let detectedMimeType = mimeType;
          const dataUrlMatch = base64String.match(/data:([^;]+);/);
          if (dataUrlMatch && validTypes.includes(dataUrlMatch[1])) {
            detectedMimeType = dataUrlMatch[1];
          }
          newImages.push({ dataUrl: base64String, mimeType: detectedMimeType });
          finishReader();
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images. Please try again.');
      setUploading(false);
    }
  };

  const removeImage = (index: number) => {
    setCurrentImages(currentImages.filter((_, i) => i !== index));
  };

  const handleVerify = () => {
    if (currentImages.length > 0) {
      verifyMeasurements();
    } else {
      alert('Please upload at least one image to verify measurements.');
    }
  };

  const itemRows =
    serviceType === 'carpet'
      ? getRawCarpetRows(userMeasurements)
      : serviceType === 'curtain'
      ? getRawCurtainRows(userMeasurements)
      : [];

  const addItemSlot = () => {
    if (!multiItemMode || !onMeasurementChange) return;
    if (itemRows.length >= MAX_ITEM_SLOTS) return;
    const nextRows =
      serviceType === 'carpet'
        ? [...getRawCarpetRows(userMeasurements), emptyCarpetRow()]
        : [...getRawCurtainRows(userMeasurements), emptyCurtainRow()];
    const nextSlot = nextRows.length - 1;
    onMeasurementChange({
      ...userMeasurements,
      measurementUnit: 'feet',
      ...(serviceType === 'carpet'
        ? { carpetItems: nextRows as CarpetItem[] }
        : { curtainItems: nextRows as CurtainItem[] }),
      images: allSlotImageUrls,
    });
    setActiveSlot(nextSlot);
    setSlotImages((prev) => ({ ...prev, [nextSlot]: [] }));
    setVerificationResults([]);
    setAllVerified(false);
    onVerificationCompleteRef.current(false, [], {
      verified: false,
      matches: false,
      aiMeasurements: {},
      userMeasurements,
      discrepancies: [],
      confidence: 0,
      verificationResults: [],
      comparisons: [],
    });
  };

  const removeItemSlot = (slotIndex: number) => {
    if (!multiItemMode || !onMeasurementChange || itemRows.length <= 1) return;
    const nextRows = itemRows.filter((_, i) => i !== slotIndex);
    const nextSlotImages: Record<number, SlotImage[]> = {};
    nextRows.forEach((_, i) => {
      const sourceIdx = i >= slotIndex ? i + 1 : i;
      nextSlotImages[i] = slotImages[sourceIdx] ?? [];
    });
    setSlotImages(nextSlotImages);
    setActiveSlot((prev) => Math.min(prev, nextRows.length - 1));
    onMeasurementChange({
      ...userMeasurements,
      measurementUnit: 'feet',
      ...(serviceType === 'carpet'
        ? { carpetItems: nextRows as CarpetItem[] }
        : { curtainItems: nextRows as CurtainItem[] }),
      images: Object.values(nextSlotImages)
        .flat()
        .map((img) => img.dataUrl),
    });
    setVerificationResults([]);
    setAllVerified(false);
    onVerificationCompleteRef.current(false, [], {
      verified: false,
      matches: false,
      aiMeasurements: {},
      userMeasurements,
      discrepancies: [],
      confidence: 0,
      verificationResults: [],
      comparisons: [],
    });
  };

  const updateMeasurementsIfAiHigher = (
    userMeas: ServiceMeasurementData,
    aiMeas: any,
    type: string
  ) => {
    if (!onMeasurementChange) return;

    const updatedMeasurements: Partial<ServiceMeasurementData> = {};

    switch (type) {
      case 'sofa':
        const userSeats = userMeas.numberOfSeats || 0;
        const aiSeats = aiMeas.seats || 0;
        const aiWidth = aiMeas.widthFt || 0;

        // If AI seats are more than user seats, use AI value
        if (aiSeats > userSeats) {
          updatedMeasurements.numberOfSeats = aiSeats;
        }

        // If AI width is more than user total width, update seat measurements
        if (aiWidth > 0 && userMeas.seatMeasurements) {
          const avgUserWidth = userMeas.seatMeasurements.reduce((sum, s) => sum + s.width, 0) / userMeas.seatMeasurements.length;
          const totalUserWidth = avgUserWidth * userSeats;
          
          if (aiWidth > totalUserWidth) {
            // Update seat measurements to reflect AI width
            const seatsToUse = updatedMeasurements.numberOfSeats || userSeats;
            const avgWidthPerSeat = aiWidth / seatsToUse;
            updatedMeasurements.seatMeasurements = Array.from({ length: seatsToUse }, (_, i) => ({
              seatNumber: i + 1,
              width: avgWidthPerSeat,
            }));
          }
        }
        break;

      case 'curtain': {
        // Multi-size: same precedent as carpet — we can't auto-correct
        // one AI photo against N customer rows, so only apply the legacy
        // "bump width/length/panels up to AI's reading" behaviour when
        // the booking is single-size (no curtainItems[], or one row).
        const rows = Array.isArray(userMeas.curtainItems) ? userMeas.curtainItems : [];
        if (rows.length > 1) break;
        const userWidth = rows[0]?.width ?? userMeas.curtainWidth ?? 0;
        const userLength = rows[0]?.length ?? userMeas.curtainLength ?? 0;
        const userPanels = rows[0]?.quantity ?? userMeas.numberOfPanels ?? 0;
        const aiWidthCurtain = aiMeas.widthFt || 0;
        const aiHeight = aiMeas.heightFt || 0;
        const aiPanels = aiMeas.panels || 0;
        if (aiWidthCurtain > userWidth) {
          updatedMeasurements.curtainWidth = aiWidthCurtain;
        }
        if (aiHeight > userLength) {
          updatedMeasurements.curtainLength = aiHeight;
        }
        if (aiPanels > userPanels) {
          updatedMeasurements.numberOfPanels = aiPanels;
        }
        // AI-as-source-of-truth mode hides the manual inputs, so panels
        // start at 0. If the AI gives usable dimensions but no panel count,
        // default to 1 — otherwise normalizeCurtainItems() drops the
        // zero-qty row and the measurement reads invalid, blocking Continue.
        const resolvedPanels =
          updatedMeasurements.numberOfPanels ?? userPanels;
        if (
          (updatedMeasurements.curtainWidth || userWidth) > 0 &&
          (updatedMeasurements.curtainLength || userLength) > 0 &&
          resolvedPanels <= 0
        ) {
          updatedMeasurements.numberOfPanels = 1;
        }
        break;
      }

      case 'carpet': {
        // Multi-size carpet bookings carry an array of independent rows;
        // we can't auto-correct one AI photo against N customer entries
        // without ambiguity. Only apply the legacy "bump width/length up
        // to AI's value" behaviour when the booking is still in the
        // single-size shape (no carpetItems[], or just one row).
        const rows = Array.isArray(userMeas.carpetItems) ? userMeas.carpetItems : [];
        if (rows.length > 1) break;
        const userCarpetWidth = rows[0]?.width ?? userMeas.carpetWidth ?? 0;
        const userCarpetLength = rows[0]?.length ?? userMeas.carpetLength ?? 0;
        const aiCarpetWidth = aiMeas.widthFt || 0;
        const aiCarpetLength = aiMeas.lengthFt || 0;
        if (aiCarpetWidth > userCarpetWidth) {
          updatedMeasurements.carpetWidth = aiCarpetWidth;
        }
        if (aiCarpetLength > userCarpetLength) {
          updatedMeasurements.carpetLength = aiCarpetLength;
        }
        // AI-as-source-of-truth mode hides the manual inputs, so the count
        // starts at 0 and the AI photo only yields ONE carpet's dimensions
        // (it can't infer how many rugs you own). Default the count to 1
        // whenever AI gives usable dimensions but no count was entered —
        // otherwise normalizeCarpetItems() filters the zero-qty row out and
        // the measurement reads as invalid, blocking Continue.
        const existingCarpetCount = rows[0]?.quantity ?? userMeas.numberOfCarpets ?? 0;
        if (
          (updatedMeasurements.carpetWidth || userCarpetWidth) > 0 &&
          (updatedMeasurements.carpetLength || userCarpetLength) > 0 &&
          existingCarpetCount <= 0
        ) {
          updatedMeasurements.numberOfCarpets = 1;
        }
        break;
      }

      case 'upholstery':
        const userQty = userMeas.quantity || 0;
        const aiQty = aiMeas.count || 0;

        if (aiQty > userQty) {
          updatedMeasurements.quantity = aiQty;
        }
        break;
    }

    // Only update if there are changes
    if (Object.keys(updatedMeasurements).length > 0) {
      onMeasurementChange({
        ...userMeas,
        ...updatedMeasurements,
      });
    }
  };

  // True when the AI result carries the measurement the booked service needs
  // to price (seats for sofa, count for upholstery items, both dimensions for
  // carpet/curtain). Used to tell "measured fine" apart from "analysed but the
  // photo doesn't match this service" (e.g. ottomans on a sofa booking → seats 0).
  const isAiMeasurementUsable = (aiMeas: any, type: string): boolean => {
    if (!aiMeas) return false;
    switch (type) {
      case 'sofa':
        return (aiMeas.seats || 0) > 0;
      case 'upholstery':
        return (aiMeas.count || 0) > 0;
      case 'carpet':
        return (aiMeas.widthFt || 0) > 0 && (aiMeas.lengthFt || 0) > 0;
      case 'curtain':
        return (aiMeas.widthFt || 0) > 0 && (aiMeas.heightFt || 0) > 0;
      default:
        return false;
    }
  };

  // Source-of-truth mode: overwrite the measurements with the AI's reading so
  // the price reflects the photo exactly (the customer never typed values in
  // this mode). AI vision returns feet, so we pin measurementUnit to 'feet'
  // to keep the area math consistent. Carpet/curtain use the preferred
  // *Items[] shape so the pricing helpers read a single AI-sized row.
  const applyAiAsSourceOfTruth = (
    userMeas: ServiceMeasurementData,
    aiMeas: any,
    type: string,
  ): ServiceMeasurementData | null => {
    if (!onMeasurementChange) return null;
    const next: ServiceMeasurementData = { ...userMeas };

    switch (type) {
      case 'sofa': {
        const seats = aiMeas.seats || 0;
        if (seats <= 0) return null;
        next.numberOfSeats = seats;
        const widthPerSeat = aiMeas.widthFt > 0 ? aiMeas.widthFt / seats : 2;
        next.seatMeasurements = Array.from({ length: seats }, (_, i) => ({
          seatNumber: i + 1,
          width: widthPerSeat,
        }));
        break;
      }
      case 'upholstery': {
        const count = aiMeas.count || 0;
        if (count <= 0) return null;
        next.quantity = count;
        break;
      }
      case 'carpet': {
        const width = aiMeas.widthFt || 0;
        const length = aiMeas.lengthFt || 0;
        if (width <= 0 || length <= 0) return null;
        next.measurementUnit = 'feet';
        const existing = [...getRawCarpetRows(userMeas)];
        while (existing.length <= activeSlot) existing.push(emptyCarpetRow());
        existing[activeSlot] = {
          ...existing[activeSlot],
          label:
            existing[activeSlot].label ||
            (existing.length > 1 ? `${itemNoun} ${activeSlot + 1}` : ''),
          width,
          length,
          quantity: existing[activeSlot].quantity || 1,
        };
        next.carpetItems = existing;
        const valid = existing.filter((r) => r.width > 0 && r.length > 0);
        next.carpetWidth = valid[0]?.width || width;
        next.carpetLength = valid[0]?.length || length;
        next.numberOfCarpets = valid.length;
        break;
      }
      case 'curtain': {
        const width = aiMeas.widthFt || 0;
        const height = aiMeas.heightFt || 0;
        if (width <= 0 || height <= 0) return null;
        const panels = aiMeas.panels && aiMeas.panels > 0 ? aiMeas.panels : 1;
        next.measurementUnit = 'feet';
        const existing = [...getRawCurtainRows(userMeas)];
        while (existing.length <= activeSlot) existing.push(emptyCurtainRow());
        existing[activeSlot] = {
          ...existing[activeSlot],
          label:
            existing[activeSlot].label ||
            (existing.length > 1 ? `${itemNoun} ${activeSlot + 1}` : ''),
          width,
          length: height,
          quantity: panels,
        };
        next.curtainItems = existing;
        const valid = existing.filter((r) => r.width > 0 && r.length > 0);
        next.curtainWidth = valid[0]?.width || width;
        next.curtainLength = valid[0]?.length || height;
        next.numberOfPanels = valid.reduce((s, r) => s + (r.quantity || 0), 0);
        break;
      }
      default:
        return null;
    }

    onMeasurementChange(next);
    return next;
  };

  const verifyMeasurements = async () => {
    if (!currentImages || currentImages.length === 0) {
      setAllVerified(true);
      return;
    }

    setVerifying(true);
    setServiceMismatch(false);
    const results: AIVerificationResult[] = [];
    const imageErrors: Array<{
      imageIndex: number;
      error: string;
    }> = [];

    try {
      // Step 1: Analyze all images and collect results
      for (let i = 0; i < currentImages.length; i++) {
        const imageData = currentImages[i];
        // Extract base64 data (remove data:image/xxx;base64, prefix)
        let imageBase64 = imageData.dataUrl;
        
        // If it's a data URL, extract just the base64 part
        if (imageData.dataUrl.includes(',')) {
          imageBase64 = imageData.dataUrl.split(',')[1];
        }
        
        // Clean the base64 string (remove whitespace)
        imageBase64 = imageBase64.trim().replace(/\s/g, '');
        
        // Normalize mimeType (jpeg -> jpeg, jpg -> jpeg)
        let mimeType = imageData.mimeType || 'image/jpeg';
        if (mimeType === 'image/jpg' || mimeType === 'image/jpeg') {
          mimeType = 'image/jpeg';
        }
        
        // Validate mimeType
        const validTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
        if (!validTypes.includes(mimeType)) {
          console.error(`Invalid mimeType: ${mimeType} for image ${i + 1}`);
          imageErrors.push({
            imageIndex: i,
            error: `Unsupported format: ${mimeType}. Please use PNG, JPEG, GIF, or WebP.`,
          });
          continue;
        }
        
        const response = await fetch('/api/ai-measure', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageBase64: imageBase64,
            itemType: aiItemType,
            mimeType: mimeType,
          }),
        });

        const result = await response.json();
        
        // Check if API call failed or returned an error
        if (!response.ok || result.error) {
          console.error('AI API error:', result.error || 'Unknown error');
          imageErrors.push({
            imageIndex: i,
            error: result.error || 'AI service unavailable',
          });
          continue;
        }
        
        // Check if this is a fallback response (AI failed)
        if (result.fallback || (result.measurements && result.measurements.confidence === 0 && result.measurements.description?.includes('unavailable'))) {
          console.error('AI analysis failed, using fallback');
          imageErrors.push({
            imageIndex: i,
            error: result.measurements?.description || 'AI analysis unavailable',
          });
          continue;
        }
        
        if (result.success && result.measurements && !result.fallback) {
          results.push(result);
        } else {
          imageErrors.push({
            imageIndex: i,
            error: 'Unable to process AI response',
          });
        }
      }

      // Step 2: Aggregate measurements from all successful results
      let aggregatedMeasurementsData: any = {};
      let totalConfidence = 0;
      let validResultsCount = 0;

      if (results.length > 0) {
        for (const result of results) {
          if (result.measurements && result.measurements.confidence > 0) {
            validResultsCount++;
            totalConfidence += result.measurements.confidence || 0;
            
            switch (serviceType) {
              case 'sofa':
                // Multiple photos of the same sofa refine the estimate — take
                // the highest reading, don't sum (extra angles ≠ extra seats).
                aggregatedMeasurementsData.seats = Math.max(
                  aggregatedMeasurementsData.seats || 0,
                  result.measurements.seats || 0,
                );
                aggregatedMeasurementsData.widthFt = Math.max(
                  aggregatedMeasurementsData.widthFt || 0,
                  result.measurements.widthFt || 0,
                );
                break;
              case 'curtain':
                // Multiple photos of the same window — max dimensions, don't sum.
                aggregatedMeasurementsData.heightFt = Math.max(
                  aggregatedMeasurementsData.heightFt || 0,
                  result.measurements.heightFt || 0,
                );
                aggregatedMeasurementsData.widthFt = Math.max(
                  aggregatedMeasurementsData.widthFt || 0,
                  result.measurements.widthFt || 0,
                );
                aggregatedMeasurementsData.panels = Math.max(
                  aggregatedMeasurementsData.panels || 0,
                  result.measurements.panels || 0,
                );
                aggregatedMeasurementsData.sqft =
                  (aggregatedMeasurementsData.heightFt || 0) *
                  (aggregatedMeasurementsData.widthFt || 0);
                break;
              case 'carpet':
                // Multiple photos of the same carpet — max dimensions per slot.
                aggregatedMeasurementsData.lengthFt = Math.max(
                  aggregatedMeasurementsData.lengthFt || 0,
                  result.measurements.lengthFt || 0,
                );
                aggregatedMeasurementsData.widthFt = Math.max(
                  aggregatedMeasurementsData.widthFt || 0,
                  result.measurements.widthFt || 0,
                );
                aggregatedMeasurementsData.sqft =
                  (aggregatedMeasurementsData.lengthFt || 0) *
                  (aggregatedMeasurementsData.widthFt || 0);
                break;
              case 'upholstery':
                // Multiple photos of the same items — max count, don't sum.
                aggregatedMeasurementsData.count = Math.max(
                  aggregatedMeasurementsData.count || 0,
                  result.measurements.count || 0,
                );
                break;
            }
          }
        }
        
        // Calculate average confidence
        aggregatedMeasurementsData.confidence = validResultsCount > 0 ? Math.round(totalConfidence / validResultsCount) : 0;
        aggregatedMeasurementsData.description = multiItemMode
          ? `Analyzed ${results.length} image${results.length !== 1 ? 's' : ''} for ${itemNoun} ${activeSlot + 1} — best estimate from all angles.`
          : `Analyzed ${results.length} image${results.length !== 1 ? 's' : ''} — best estimate from all angles.`;
        setAggregatedMeasurements(aggregatedMeasurementsData);
      }

      // Step 3: Compare aggregated measurements with user measurements
      const comparisons: Array<{
        imageIndex: number;
        matches: boolean;
        discrepancies: Array<{ field: string; userValue: string; aiValue: string; difference: string }>;
        confidence: number;
      }> = [];

      if (imageErrors.length > 0) {
        // Add errors for failed images
        for (const error of imageErrors) {
          comparisons.push({
            imageIndex: error.imageIndex,
            matches: false,
            discrepancies: [{
              field: 'AI Analysis',
              userValue: 'N/A',
              aiValue: 'Failed',
              difference: error.error,
            }],
            confidence: 0,
          });
        }
      }

      // Add aggregated comparison if we have valid results
      if (Object.keys(aggregatedMeasurementsData).length > 0) {
        const comparison = compareMeasurements(userMeasurements, aggregatedMeasurementsData, serviceType);
        comparisons.push({
          imageIndex: -1, // -1 indicates aggregated result
          matches: comparison.matches,
          discrepancies: comparison.discrepancies,
          confidence: aggregatedMeasurementsData.confidence || 0,
        });
      }

      setVerificationResults(results);
      setComparisons(comparisons);
      
      // Check if verification matches (only check aggregated result, ignore individual image errors if we have aggregated data)
      const aggregatedComparison = comparisons.find(c => c.imageIndex === -1);
      // Only set allVerified to true if there are NO discrepancies (matches === true)
      const allMatch = aggregatedComparison 
        ? aggregatedComparison.matches && aggregatedComparison.discrepancies.length === 0
        : comparisons.length > 0 ? comparisons.every(c => c.matches && c.discrepancies.length === 0) : false;

      const analysisSucceeded =
        results.length > 0 && (aggregatedMeasurementsData.confidence || 0) > 0;

      if (aiSourceOfTruth) {
        // AI is the source of truth: "verified" means we got a usable reading
        // we could price. If the analysis worked but yielded no usable
        // measurement for this service, treat it as a service/photo mismatch
        // (don't apply, don't price, warn) rather than a false green.
        const slotUsable = isAiMeasurementUsable(aggregatedMeasurementsData, serviceType);
        const mismatch = analysisSucceeded && !slotUsable;
        setServiceMismatch(mismatch);
        const appliedMeas = slotUsable
          ? applyAiAsSourceOfTruth(userMeasurements, aggregatedMeasurementsData, serviceType)
          : null;
        const allComplete =
          multiItemMode && (serviceType === 'carpet' || serviceType === 'curtain')
            ? !!(appliedMeas && allItemSlotsMeasured(appliedMeas, serviceType))
            : slotUsable;
        setAllVerified(allComplete);
        // Auto-confirm when every item slot is measured (or single-item
        // services get a usable reading). Partial multi-item progress keeps
        // Continue locked until each carpet/curtain is photographed.
        onVerificationComplete(allComplete, results, {
          verified: allComplete,
          matches: allComplete,
          aiMeasurements: aggregatedMeasurementsData,
          userMeasurements: appliedMeas || userMeasurements,
          discrepancies: aggregatedComparison?.discrepancies || [],
          confidence: aggregatedComparison?.confidence || 0,
          verificationResults: results,
          comparisons,
        });
      } else {
        setServiceMismatch(false);
        setAllVerified(allMatch);
        // Verify mode: only raise the value if the AI read a higher one.
        if (aggregatedComparison && Object.keys(aggregatedMeasurementsData).length > 0) {
          updateMeasurementsIfAiHigher(userMeasurements, aggregatedMeasurementsData, serviceType);
        }
      }
      // (Removed stray setVerificationComplete(true) — no such state/setter
      // exists in this component; it threw ReferenceError the moment AI
      // verification finished. Completion is already reflected by
      // verificationResults/comparisons/allVerified + setVerifying(false), and
      // the parent is notified via the Continue/Skip buttons.)
    } catch (error) {
      console.error('Verification error:', error);
      setAllVerified(false); // Don't show verified message if verification fails
    } finally {
      setVerifying(false);
    }
  };

  const compareMeasurements = (
    userMeas: ServiceMeasurementData,
    aiMeas: any,
    type: string
  ): { matches: boolean; discrepancies: Array<{ field: string; userValue: string; aiValue: string; difference: string }> } => {
    const discrepancies: Array<{ field: string; userValue: string; aiValue: string; difference: string }> = [];
    const tolerance = 0.2; // 20% tolerance for measurements

    switch (type) {
      case 'sofa':
        const userSeats = userMeas.numberOfSeats || 0;
        const aiSeats = aiMeas.seats || 0;
        const seatDiff = Math.abs(userSeats - aiSeats);
        
        if (seatDiff > 0) {
          discrepancies.push({
            field: 'Number of Seats',
            userValue: userSeats.toString(),
            aiValue: aiSeats.toString(),
            difference: `${seatDiff} seat${seatDiff !== 1 ? 's' : ''}`,
          });
        }

        // Compare seat widths if available
        if (userMeas.seatMeasurements && aiMeas.widthFt) {
          const avgUserWidth = userMeas.seatMeasurements.reduce((sum, s) => sum + s.width, 0) / userMeas.seatMeasurements.length;
          const totalUserWidth = avgUserWidth * userSeats;
          const widthDiff = Math.abs(totalUserWidth - aiMeas.widthFt);
          
          if (widthDiff > tolerance * aiMeas.widthFt) {
            discrepancies.push({
              field: 'Total Width',
              userValue: `${totalUserWidth.toFixed(1)} ft`,
              aiValue: `${aiMeas.widthFt.toFixed(1)} ft`,
              difference: `${widthDiff.toFixed(1)} ft`,
            });
          }
        }
        break;

      case 'curtain': {
        // Skip per-dimension comparison for multi-size curtain bookings —
        // one AI photo against N rows is ambiguous. Keep the legacy
        // single-size comparison alive for one-row bookings (covers
        // pre-feature data too).
        const rows = Array.isArray(userMeas.curtainItems) ? userMeas.curtainItems : [];
        if (rows.length > 1) break;
        const userWidth = rows[0]?.width ?? userMeas.curtainWidth ?? 0;
        const userLength = rows[0]?.length ?? userMeas.curtainLength ?? 0;
        const userPanels = rows[0]?.quantity ?? userMeas.numberOfPanels ?? 0;
        const aiWidth = aiMeas.widthFt || 0;
        const aiHeight = aiMeas.heightFt || 0;
        const aiPanels = aiMeas.panels || 0;
        if (Math.abs(userWidth - aiWidth) > tolerance * Math.max(userWidth, aiWidth)) {
          discrepancies.push({
            field: 'Width',
            userValue: `${userWidth.toFixed(1)} ft`,
            aiValue: `${aiWidth.toFixed(1)} ft`,
            difference: `${Math.abs(userWidth - aiWidth).toFixed(1)} ft`,
          });
        }
        if (Math.abs(userLength - aiHeight) > tolerance * Math.max(userLength, aiHeight)) {
          discrepancies.push({
            field: 'Length/Height',
            userValue: `${userLength.toFixed(1)} ft`,
            aiValue: `${aiHeight.toFixed(1)} ft`,
            difference: `${Math.abs(userLength - aiHeight).toFixed(1)} ft`,
          });
        }
        if (Math.abs(userPanels - aiPanels) > 0) {
          discrepancies.push({
            field: 'Number of Panels',
            userValue: userPanels.toString(),
            aiValue: aiPanels.toString(),
            difference: `${Math.abs(userPanels - aiPanels)} panel${Math.abs(userPanels - aiPanels) !== 1 ? 's' : ''}`,
          });
        }
        break;
      }

      case 'carpet': {
        // Skip per-dimension discrepancy reporting for multi-size carpet
        // bookings — comparing one AI photo against N different rows is
        // ambiguous (which row does the photo represent?). Keep the
        // legacy comparison alive when there's exactly one row, which is
        // also how every pre-feature booking will look.
        const rows = Array.isArray(userMeas.carpetItems) ? userMeas.carpetItems : [];
        if (rows.length > 1) break;
        const userCarpetWidth = rows[0]?.width ?? userMeas.carpetWidth ?? 0;
        const userCarpetLength = rows[0]?.length ?? userMeas.carpetLength ?? 0;
        const aiCarpetWidth = aiMeas.widthFt || 0;
        const aiCarpetLength = aiMeas.lengthFt || 0;
        if (
          Math.abs(userCarpetWidth - aiCarpetWidth) >
          tolerance * Math.max(userCarpetWidth, aiCarpetWidth)
        ) {
          discrepancies.push({
            field: 'Width',
            userValue: `${userCarpetWidth.toFixed(1)} ft`,
            aiValue: `${aiCarpetWidth.toFixed(1)} ft`,
            difference: `${Math.abs(userCarpetWidth - aiCarpetWidth).toFixed(1)} ft`,
          });
        }
        if (
          Math.abs(userCarpetLength - aiCarpetLength) >
          tolerance * Math.max(userCarpetLength, aiCarpetLength)
        ) {
          discrepancies.push({
            field: 'Length',
            userValue: `${userCarpetLength.toFixed(1)} ft`,
            aiValue: `${aiCarpetLength.toFixed(1)} ft`,
            difference: `${Math.abs(userCarpetLength - aiCarpetLength).toFixed(1)} ft`,
          });
        }
        break;
      }

      case 'upholstery':
        const userQty = userMeas.quantity || 0;
        const aiQty = aiMeas.count || 0;
        
        if (Math.abs(userQty - aiQty) > 0) {
          discrepancies.push({
            field: 'Quantity',
            userValue: userQty.toString(),
            aiValue: aiQty.toString(),
            difference: `${Math.abs(userQty - aiQty)} item${Math.abs(userQty - aiQty) !== 1 ? 's' : ''}`,
          });
        }
        break;
    }

    return {
      matches: discrepancies.length === 0,
      discrepancies,
    };
  };

  const handleContinue = () => {
    // Prepare AI verification data to pass to parent
    const aggregatedComparison = comparisons.find(c => c.imageIndex === -1);
    
    const aiVerificationData = {
      verified: allVerified,
      matches: aggregatedComparison?.matches || false,
      aiMeasurements: aggregatedMeasurements,
      userMeasurements: userMeasurements,
      discrepancies: aggregatedComparison?.discrepancies || [],
      confidence: aggregatedComparison?.confidence || 0,
      verificationResults: verificationResults,
      comparisons: comparisons,
    };
    
    onVerificationComplete(allVerified, verificationResults, aiVerificationData);
  };

  const handleSkip = () => {
    setAllVerified(true);
    // Pass empty verification data when skipped
    onVerificationComplete(true, verificationResults, {
      verified: false,
      matches: false,
      aiMeasurements: {},
      userMeasurements: userMeasurements,
      discrepancies: [],
      confidence: 0,
      verificationResults: [],
      comparisons: [],
    });
  };

  return (
    <motion.div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1158:4" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1158" data-component-end-line="1685" data-component-file="MeasurementVerificationStep.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22space-y-8%22%7D"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      {!embedded && (
        <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1165:8" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1165" data-component-end-line="1175" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <span data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1166:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1166" data-component-end-line="1168" data-component-file="MeasurementVerificationStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%20tracking-widest%20uppercase%20text-%5B%23204653%5D%20mb-3%20block%22%2C%22textContent%22%3A%22Step%203%22%7D" className="text-sm font-medium tracking-widest uppercase text-[#204653] mb-3 block">
            Step 3
          </span>
          <h2 data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1169:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1169" data-component-end-line="1171" data-component-file="MeasurementVerificationStep.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20md%3Atext-4xl%20font-semibold%20text-%5B%231D1D1F%5D%20mb-3%20font-heading%20tracking-tight%22%2C%22textContent%22%3A%22Verify%20Measurements%22%7D" className="text-3xl md:text-4xl font-semibold text-[#1D1D1F] mb-3 font-heading tracking-tight">
            Verify Measurements
          </h2>
          <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1172:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1172" data-component-end-line="1174" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B%2386868B%5D%20text-lg%22%2C%22textContent%22%3A%22Upload%20photos%20to%20verify%20your%20measurements%20using%20AI%22%7D" className="text-[#86868B] text-lg">
            Upload photos to verify your {serviceName} measurements using AI
          </p>
        </div>
      )}

      <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1178:6" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1178" data-component-end-line="1188" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20bg-amber-50%20border%20border-amber-200%20rounded-xl%20flex%20items-start%20gap-3%22%7D" className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
        <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1179:8" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1179" data-component-end-line="1179" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22InformationCircleIcon%22%2C%22className%22%3A%22text-amber-600%20flex-shrink-0%20mt-0.5%22%7D" name="InformationCircleIcon" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1180:8" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1180" data-component-end-line="1187" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1181:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1181" data-component-end-line="1183" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20font-semibold%20text-amber-900%22%2C%22textContent%22%3A%22AI%20estimate%20is%20approximate%22%7D" className="text-sm font-semibold text-amber-900">
            AI estimate is approximate
          </p>
          <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1184:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1184" data-component-end-line="1186" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-amber-800%20mt-1%22%2C%22textContent%22%3A%22This%20is%20a%20rough%20estimate%20based%20on%20your%20photos.%20The%20final%20amount%20will%20be%20confirmed%20by%20our%20applicator%20after%20on-site%20measurement.%22%7D" className="text-xs text-amber-800 mt-1">
            This is a rough estimate based on your photos. The final amount will be confirmed by our applicator after on-site measurement.
          </p>
        </div>
      </div>

      {/* Image Upload Section */}
      <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1191:6" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1191" data-component-end-line="1330" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
        <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1192:8" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1192" data-component-end-line="1213" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <h3 data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1193:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1193" data-component-end-line="1195" data-component-file="MeasurementVerificationStep.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-semibold%20text-%5B%231D1D1F%5D%20mb-2%22%2C%22textContent%22%3A%22Upload%20Photos%20for%20Verification%22%7D" className="text-xl font-semibold text-[#1D1D1F] mb-2">
            Upload Photos for Verification
          </h3>
          <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1196:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1196" data-component-end-line="1198" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22Upload%20photos%20to%20help%20us%20verify%20the%20number%20of%20and%20measurements%22%7D" className="text-sm text-[#86868B]">
            Upload photos to help us verify the number of {serviceType === 'sofa' ? 'seats' : serviceType === 'curtain' ? 'panels' : serviceType === 'carpet' ? 'carpets' : 'items'} and measurements
          </p>
          {aiSourceOfTruth && (serviceType === 'carpet' || serviceType === 'curtain') && (
            <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1200:12" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1200" data-component-end-line="1205" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%23204653%5D%20mt-1%20font-medium%22%2C%22textContent%22%3A%22Upload%20multiple%20photos%20of%20the%20same%20from%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20different%20angles%20%E2%80%94%20extra%20photos%20improve%20accuracy%20without%20adding%20to%20the%20price.%20Need%20a%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20different%20%3F%20Use%20%5C%22Add%20another%20%20%20%5C%22%20below.%22%7D" className="text-xs text-[#204653] mt-1 font-medium">
              Upload multiple photos of the same {serviceType === 'curtain' ? 'curtain' : 'carpet'} from
              different angles — extra photos improve accuracy without adding to the price. Need a
              different {serviceType === 'curtain' ? 'curtain' : 'carpet'}? Use &quot;Add another{' '}
              {serviceType === 'curtain' ? 'curtain' : 'carpet'}&quot; below.
            </p>
          )}
          {aiSourceOfTruth && serviceType !== 'carpet' && serviceType !== 'curtain' && (
            <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1208:12" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1208" data-component-end-line="1211" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%23204653%5D%20mt-1%20font-medium%22%2C%22textContent%22%3A%22You%20can%20upload%20multiple%20photos%20from%20different%20angles%20%E2%80%94%20extra%20photos%20improve%20accuracy%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20without%20increasing%20the%20price.%22%7D" className="text-xs text-[#204653] mt-1 font-medium">
              You can upload multiple photos from different angles — extra photos improve accuracy
              without increasing the price.
            </p>
          )}
        </div>

        {multiItemMode && (
          <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1216:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1216" data-component-end-line="1258" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">
            <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1217:12" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1217" data-component-end-line="1249" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-2%22%7D" className="flex flex-wrap gap-2">
              {itemRows.map((row, idx) => {
                const measured = row.width > 0 && row.length > 0;
                const active = activeSlot === idx;
                return (
                  <button data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1222:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1222" data-component-end-line="1236" data-component-file="MeasurementVerificationStep.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
                    key={idx}
                    type="button"
                    onClick={() => setActiveSlot(idx)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                      active
                        ? 'bg-[#204653] text-white border-[#204653]'
                        : 'bg-white text-[#1D1D1F] border-gray-200 hover:border-[#204653]/40'
                    }`}
                  >
                    {itemNoun} {idx + 1}
                    {measured && (
                      <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1234:22" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1234" data-component-end-line="1234" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%7D" name="CheckCircleIcon" size={14} className={active ? 'text-white' : 'text-green-600'} />
                    )}
                  </button>
                );
              })}
              {itemRows.length < MAX_ITEM_SLOTS && (
                <button data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1240:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1240" data-component-end-line="1247" data-component-file="MeasurementVerificationStep.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22inline-flex%20items-center%20gap-1%20px-3%20py-1.5%20rounded-full%20text-sm%20font-semibold%20border-2%20border-dashed%20border-%5B%23204653%5D%2F40%20text-%5B%23204653%5D%20hover%3Abg-%5B%23F5FAF9%5D%22%2C%22textContent%22%3A%22Add%20another%22%7D"
                  type="button"
                  onClick={addItemSlot}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-semibold border-2 border-dashed border-[#204653]/40 text-[#204653] hover:bg-[#F5FAF9]"
                >
                  <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1245:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1245" data-component-end-line="1245" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22PlusIcon%22%7D" name="PlusIcon" size={14} />
                  Add another {serviceType === 'curtain' ? 'curtain' : 'carpet'}
                </button>
              )}
            </div>
            {itemRows.length > 1 && (
              <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1251:14" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1251" data-component-end-line="1256" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22Photos%20below%20apply%20to%22%7D" className="text-xs text-[#86868B]">
                Photos below apply to <span data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1252:38" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1252" data-component-end-line="1252" data-component-file="MeasurementVerificationStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-%5B%231D1D1F%5D%22%7D" className="font-semibold text-[#1D1D1F]">{itemNoun} {activeSlot + 1}</span>
                {itemRows[activeSlot]?.width > 0 && itemRows[activeSlot]?.length > 0
                  ? ` (${itemRows[activeSlot].width}×${itemRows[activeSlot].length} ft)`
                  : ' — not measured yet'}
              </p>
            )}
          </div>
        )}

        <input data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1261:8" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1261" data-component-end-line="1268" data-component-file="MeasurementVerificationStep.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22file%22%2C%22className%22%3A%22hidden%22%7D"
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageUpload}
          className="hidden"
        />

        <button data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1270:8" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1270" data-component-end-line="1289" data-component-file="MeasurementVerificationStep.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20p-6%20border-2%20border-dashed%20border-gray-300%20rounded-xl%20hover%3Aborder-%5B%23204653%5D%20hover%3Abg-gray-50%20transition-colors%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%22%7D"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || verifying}
          className="w-full p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#204653] hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1275:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1275" data-component-end-line="1288" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20gap-2%22%7D" className="flex flex-col items-center gap-2">
            {uploading ? (
              <>
                <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1278:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1278" data-component-end-line="1278" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowPathIcon%22%2C%22className%22%3A%22text-%5B%23204653%5D%20animate-spin%22%7D" name="ArrowPathIcon" size={32} className="text-[#204653] animate-spin" />
                <span data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1279:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1279" data-component-end-line="1279" data-component-file="MeasurementVerificationStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22Uploading...%22%7D" className="text-sm text-[#86868B]">Uploading...</span>
              </>
            ) : (
              <>
                <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1283:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1283" data-component-end-line="1283" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CameraIcon%22%2C%22className%22%3A%22text-%5B%23204653%5D%22%7D" name="CameraIcon" size={32} className="text-[#204653]" />
                <span data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1284:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1284" data-component-end-line="1284" data-component-file="MeasurementVerificationStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%20text-%5B%231D1D1F%5D%22%2C%22textContent%22%3A%22Click%20to%20upload%20photos%22%7D" className="text-sm font-medium text-[#1D1D1F]">Click to upload photos</span>
                <span data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1285:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1285" data-component-end-line="1285" data-component-file="MeasurementVerificationStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22Maximum%201%20MB%20per%20image%22%7D" className="text-xs text-[#86868B]">Maximum 1 MB per image</span>
              </>
            )}
          </div>
        </button>

        {currentImages.length > 0 && (
          <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1292:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1292" data-component-end-line="1328" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
            <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1293:12" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1293" data-component-end-line="1311" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20md%3Agrid-cols-3%20gap-4%22%7D" className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {currentImages.map((image, index) => (
                <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1295:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1295" data-component-end-line="1309" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20group%22%7D" key={index} className="relative group">
                  <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1296:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1296" data-component-end-line="1302" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22aspect-square%20rounded-lg%20overflow-hidden%20border-2%20border-gray-200%22%7D" className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200">
                    <img data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1297:20" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1297" data-component-end-line="1301" data-component-file="MeasurementVerificationStep.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22className%22%3A%22w-full%20h-full%20object-cover%22%7D"
                      src={image.dataUrl}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1303:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1303" data-component-end-line="1308" data-component-file="MeasurementVerificationStep.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20top-2%20right-2%20p-1.5%20bg-red-500%20text-white%20rounded-full%20opacity-0%20group-hover%3Aopacity-100%20transition-opacity%22%7D"
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1307:20" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1307" data-component-end-line="1307" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={16} />
                  </button>
                </div>
              ))}
            </div>
            {!(aiSourceOfTruth && allVerified) && (
            <button data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1313:12" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1313" data-component-end-line="1326" data-component-file="MeasurementVerificationStep.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20px-6%20py-3.5%20rounded-full%20bg-%5B%23204653%5D%20text-white%20font-semibold%20hover%3Abg-%5B%231a3a44%5D%20transition-all%20duration-200%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%22%7D"
              onClick={handleVerify}
              disabled={verifying || currentImages.length === 0}
              className="w-full px-6 py-3.5 rounded-full bg-[#204653] text-white font-semibold hover:bg-[#1a3a44] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {verifying ? (
                <span data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1319:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1319" data-component-end-line="1322" data-component-file="MeasurementVerificationStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20gap-2%22%2C%22textContent%22%3A%22Verifying%20with%20AI...%22%7D" className="flex items-center justify-center gap-2">
                  <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1320:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1320" data-component-end-line="1320" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowPathIcon%22%2C%22className%22%3A%22animate-spin%22%7D" name="ArrowPathIcon" size={20} className="animate-spin" />
                  Verifying with AI...
                </span>
              ) : (
                'Verify Measurements with AI'
              )}
            </button>
            )}
          </div>
        )}
      </div>

      {verifying ? (
        <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1333:8" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1333" data-component-end-line="1337" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-12%20bg-white%20border-2%20border-gray-200%20rounded-2xl%20text-center%22%7D" className="p-12 bg-white border-2 border-gray-200 rounded-2xl text-center">
          <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1334:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1334" data-component-end-line="1334" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowPathIcon%22%2C%22className%22%3A%22mx-auto%20mb-4%20text-%5B%23204653%5D%20animate-spin%22%7D" name="ArrowPathIcon" size={64} className="mx-auto mb-4 text-[#204653] animate-spin" />
          <h3 data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1335:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1335" data-component-end-line="1335" data-component-file="MeasurementVerificationStep.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-semibold%20text-%5B%231D1D1F%5D%20mb-2%22%2C%22textContent%22%3A%22Analyzing%20Images...%22%7D" className="text-xl font-semibold text-[#1D1D1F] mb-2">Analyzing Images...</h3>
          <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1336:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1336" data-component-end-line="1336" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22Using%20AI%20to%20verify%20your%20measurements%22%7D" className="text-[#86868B]">Using AI to verify your measurements</p>
        </div>
      ) : verificationResults.length > 0 && !verifying ? (
        <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1339:8" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1339" data-component-end-line="1636" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
          {/* Service / photo mismatch — AI analysed the image but couldn't read
              a usable measurement for the booked service (e.g. the photo shows
              a different item). Shown instead of a misleading "Verified ✓", and
              the price intentionally stays unset until a matching photo. */}
          {serviceMismatch && (
            <motion.div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1345:12" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1345" data-component-end-line="1375" data-component-file="MeasurementVerificationStep.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22p-6%20rounded-2xl%20border-2%20bg-amber-50%20border-amber-200%22%7D"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 rounded-2xl border-2 bg-amber-50 border-amber-200"
            >
              <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1350:14" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1350" data-component-end-line="1374" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-4%22%7D" className="flex items-start gap-4">
                <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1351:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1351" data-component-end-line="1353" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-3%20rounded-xl%20bg-amber-100%22%7D" className="p-3 rounded-xl bg-amber-100">
                  <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1352:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1352" data-component-end-line="1352" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ExclamationTriangleIcon%22%2C%22className%22%3A%22text-amber-600%22%7D" name="ExclamationTriangleIcon" size={32} className="text-amber-600" />
                </div>
                <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1354:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1354" data-component-end-line="1373" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
                  <h3 data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1355:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1355" data-component-end-line="1357" data-component-file="MeasurementVerificationStep.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-%5B%231D1D1F%5D%20mb-1%22%2C%22textContent%22%3A%22This%20photo%20doesn%E2%80%99t%20match%20your%22%7D" className="text-lg font-semibold text-[#1D1D1F] mb-1">
                    This photo doesn’t match your {serviceName}
                  </h3>
                  <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1358:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1358" data-component-end-line="1367" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22We%20analysed%20the%20image%20but%20couldn%E2%80%99t%20read%20a%20%20%20%20%20for%20.%20Please%20upload%20a%20clear%20photo%20of%20your%20%20%20so%20we%20can%20price%20it%20accurately.%22%7D" className="text-sm text-[#86868B]">
                    We analysed the image but couldn’t read a{' '}
                    {serviceType === 'sofa'
                      ? 'seat count'
                      : serviceType === 'upholstery'
                      ? 'count of items'
                      : 'set of dimensions'}{' '}
                    for {serviceName}. Please upload a clear photo of your{' '}
                    {serviceName.toLowerCase()} so we can price it accurately.
                  </p>
                  {verificationResults[0]?.measurements?.description && (
                    <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1369:20" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1369" data-component-end-line="1371" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-amber-700%20mt-2%20italic%22%2C%22textContent%22%3A%22AI%20saw%3A%22%7D" className="text-xs text-amber-700 mt-2 italic">
                      AI saw: {verificationResults[0].measurements.description}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Source-of-truth summary — the customer never typed values in this
              mode, so a "you vs AI" discrepancy card makes no sense. Show what
              the AI detected and applied to the price instead. */}
          {aiSourceOfTruth && !serviceMismatch && verificationResults.length > 0 && (() => {
            const m = aggregatedMeasurements || {};
            let summary = '';
            let summaryList: string[] = [];
            switch (serviceType) {
              case 'sofa':
                summary = `${m.seats || 0} seat${(m.seats || 0) !== 1 ? 's' : ''}${
                  m.widthFt ? ` · ~${Math.round(m.widthFt)} ft wide` : ''
                }`;
                break;
              case 'upholstery':
                summary = `${m.count || 0} item${(m.count || 0) !== 1 ? 's' : ''}`;
                break;
              case 'carpet': {
                const rows = getRawCarpetRows(userMeasurements).filter(
                  (r) => r.width > 0 && r.length > 0,
                );
                if (rows.length > 1) {
                  summaryList = rows.map(
                    (r, i) =>
                      `${r.label || `Carpet ${i + 1}`}: ${r.width}×${r.length} ft`,
                  );
                } else {
                  summary = `${m.widthFt || 0}×${m.lengthFt || 0} ft`;
                }
                break;
              }
              case 'curtain': {
                const rows = getRawCurtainRows(userMeasurements).filter(
                  (r) => r.width > 0 && r.length > 0,
                );
                if (rows.length > 1) {
                  summaryList = rows.map(
                    (r, i) =>
                      `${r.label || `Curtain ${i + 1}`}: ${r.width}×${r.length} ft${
                        r.quantity ? ` · ${r.quantity} panel${r.quantity !== 1 ? 's' : ''}` : ''
                      }`,
                  );
                } else {
                  summary = `${m.widthFt || 0}×${m.heightFt || 0} ft${
                    m.panels ? ` · ${m.panels} panel${m.panels !== 1 ? 's' : ''}` : ''
                  }`;
                }
                break;
              }
            }
            return (
              <motion.div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1428:14" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1428" data-component-end-line="1482" data-component-file="MeasurementVerificationStep.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22p-6%20rounded-2xl%20border-2%20bg-green-50%20border-green-200%22%7D"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 rounded-2xl border-2 bg-green-50 border-green-200"
              >
                <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1433:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1433" data-component-end-line="1481" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-4%22%7D" className="flex items-start gap-4">
                  <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1434:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1434" data-component-end-line="1436" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-3%20rounded-xl%20bg-green-100%22%7D" className="p-3 rounded-xl bg-green-100">
                    <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1435:20" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1435" data-component-end-line="1435" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%2C%22className%22%3A%22text-green-600%22%7D" name="CheckCircleIcon" size={32} className="text-green-600" />
                  </div>
                  <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1437:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1437" data-component-end-line="1480" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
                    <h3 data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1438:20" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1438" data-component-end-line="1438" data-component-file="MeasurementVerificationStep.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-%5B%231D1D1F%5D%20mb-1%22%2C%22textContent%22%3A%22AI%20Measurement%20Applied%22%7D" className="text-lg font-semibold text-[#1D1D1F] mb-1">AI Measurement Applied</h3>
                    {m.confidence > 0 && (
                      <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1440:22" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1440" data-component-end-line="1443" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22Confidence%3A%20%25%20%7C%20Analyzed%20image%22%7D" className="text-sm text-[#86868B]">
                        Confidence: {m.confidence}% | Analyzed {verificationResults.length} image
                        {verificationResults.length !== 1 ? 's' : ''}
                      </p>
                    )}
                    {summaryList.length > 0 ? (
                      <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1446:22" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1446" data-component-end-line="1458" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20text-%5B%231D1D1F%5D%20mt-2%22%2C%22textContent%22%3A%22%E2%80%94%20the%20price%20below%20reflects%20each%3A%22%7D" className="text-sm text-[#1D1D1F] mt-2">
                        <span data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1447:24" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1447" data-component-end-line="1449" data-component-file="MeasurementVerificationStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%22%2C%22textContent%22%3A%22Detected%22%7D" className="font-semibold">
                          Detected {summaryList.length} {serviceType === 'curtain' ? 'curtains' : 'carpets'}
                        </span>{' '}
                        — the price below reflects each:
                        <ul data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1451:24" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1451" data-component-end-line="1457" data-component-file="MeasurementVerificationStep.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22mt-1%20space-y-0.5%22%7D" className="mt-1 space-y-0.5">
                          {summaryList.map((line, i) => (
                            <li data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1453:28" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1453" data-component-end-line="1455" data-component-file="MeasurementVerificationStep.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%2C%22className%22%3A%22text-%5B%231D1D1F%5D%22%2C%22textContent%22%3A%22%E2%80%A2%22%7D" key={i} className="text-[#1D1D1F]">
                              • {line}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      summary && (
                        <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1461:24" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1461" data-component-end-line="1464" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-%5B%231D1D1F%5D%20mt-2%22%2C%22textContent%22%3A%22Detected%3A%20%E2%80%94%20the%20price%20below%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20reflects%20this.%22%7D" className="text-sm text-[#1D1D1F] mt-2">
                          Detected: <span data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1462:36" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1462" data-component-end-line="1462" data-component-file="MeasurementVerificationStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%22%7D" className="font-semibold">{summary}</span> — the price below
                          reflects this.
                        </p>
                      )
                    )}
                    {verificationResults[0]?.measurements?.description && (
                      <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1468:22" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1468" data-component-end-line="1470" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%2386868B%5D%20mt-1%20italic%22%7D" className="text-xs text-[#86868B] mt-1 italic">
                        {verificationResults[0].measurements.description}
                      </p>
                    )}
                    {multiItemMode &&
                      itemRows.some((r) => r.width <= 0 || r.length <= 0) && (
                        <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1474:24" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1474" data-component-end-line="1478" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-amber-700%20mt-2%22%2C%22textContent%22%3A%22Switch%20to%20each%20remaining%20tab%2C%20upload%20photos%2C%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20and%20verify%20%E2%80%94%20Continue%20unlocks%20when%20every%20%20%20is%20measured.%22%7D" className="text-sm text-amber-700 mt-2">
                          Switch to each remaining {itemNoun.toLowerCase()} tab, upload photos,
                          and verify — Continue unlocks when every{' '}
                          {serviceType === 'curtain' ? 'curtain' : 'carpet'} is measured.
                        </p>
                      )}
                  </div>
                </div>
              </motion.div>
            );
          })()}

          {/* Show aggregated result first if multiple images */}
          {!serviceMismatch && !aiSourceOfTruth && comparisons.filter(c => c.imageIndex === -1).map((comparison, index) => (
            <motion.div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1488:12" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1488" data-component-end-line="1576" data-component-file="MeasurementVerificationStep.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%7D"
              key={`aggregated-${index}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`p-6 rounded-2xl border-2 ${
                comparison.matches
                  ? 'bg-green-50 border-green-200'
                  : comparison.confidence === 0
                  ? 'bg-red-50 border-red-200'
                  : 'bg-amber-50 border-amber-200'
              }`}
            >
              <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1501:14" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1501" data-component-end-line="1538" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-4%20mb-4%22%7D" className="flex items-start gap-4 mb-4">
                <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1502:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1502" data-component-end-line="1510" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`p-3 rounded-xl ${
                  comparison.matches ? 'bg-green-100' : comparison.confidence === 0 ? 'bg-red-100' : 'bg-amber-100'
                }`}>
                  <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1505:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1505" data-component-end-line="1509" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D"
                    name={comparison.matches ? 'CheckCircleIcon' : comparison.confidence === 0 ? 'XCircleIcon' : 'ExclamationTriangleIcon'}
                    size={32}
                    className={comparison.matches ? 'text-green-600' : comparison.confidence === 0 ? 'text-red-600' : 'text-amber-600'}
                  />
                </div>
                <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1511:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1511" data-component-end-line="1537" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
                  <h3 data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1512:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1512" data-component-end-line="1520" data-component-file="MeasurementVerificationStep.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-%5B%231D1D1F%5D%20mb-1%22%7D" className="text-lg font-semibold text-[#1D1D1F] mb-1">
                    {currentImages.length > 1 ? 'Total from All Images' : 'Image Analysis'} {
                      comparison.matches 
                        ? 'Verified ✓' 
                        : comparison.confidence === 0 
                        ? 'Analysis Failed' 
                        : 'Discrepancy Found'
                    }
                  </h3>
                  {comparison.confidence > 0 ? (
                    <>
                      <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1523:22" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1523" data-component-end-line="1525" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22Confidence%3A%20%25%20%7C%20Analyzed%20image%22%7D" className="text-sm text-[#86868B]">
                        Confidence: {comparison.confidence}% | Analyzed {verificationResults.length} image{verificationResults.length !== 1 ? 's' : ''}
                      </p>
                      <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1526:22" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1526" data-component-end-line="1530" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-%5B%231D1D1F%5D%20mt-2%22%7D" className="text-sm text-[#1D1D1F] mt-2">
                        {currentImages.length > 1 
                          ? `Cumulative measurements from ${currentImages.length} images compared with your entered values.`
                          : verificationResults[0]?.measurements?.description || 'Analysis complete'}
                      </p>
                    </>
                  ) : (
                    <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1533:20" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1533" data-component-end-line="1535" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-red-600%20font-medium%22%2C%22textContent%22%3A%22AI%20analysis%20unavailable.%20Please%20verify%20measurements%20manually%20or%20try%20again.%22%7D" className="text-sm text-red-600 font-medium">
                      AI analysis unavailable. Please verify measurements manually or try again.
                    </p>
                  )}
                </div>
              </div>

              {comparison.discrepancies.length > 0 && (
                <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1541:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1541" data-component-end-line="1566" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`mt-4 pt-4 border-t ${comparison.confidence === 0 ? 'border-red-200' : 'border-amber-200'}`}>
                  <h4 data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1542:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1542" data-component-end-line="1544" data-component-file="MeasurementVerificationStep.tsx" data-component-name="h4" data-component-content="%7B%22elementName%22%3A%22h4%22%2C%22className%22%3A%22text-sm%20font-semibold%20text-%5B%231D1D1F%5D%20mb-3%22%7D" className="text-sm font-semibold text-[#1D1D1F] mb-3">
                    {comparison.confidence === 0 ? 'Error Details:' : 'Discrepancies:'}
                  </h4>
                  <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1545:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1545" data-component-end-line="1565" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-2%22%7D" className="space-y-2">
                    {comparison.discrepancies.map((disc, discIndex) => (
                      <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1547:22" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1547" data-component-end-line="1563" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" key={discIndex} className={`flex items-center justify-between p-3 rounded-lg ${
                        comparison.confidence === 0 ? 'bg-red-100' : 'bg-white'
                      }`}>
                        <span data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1550:24" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1550" data-component-end-line="1550" data-component-file="MeasurementVerificationStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%20text-%5B%231D1D1F%5D%22%2C%22textContent%22%3A%22%3A%22%7D" className="text-sm font-medium text-[#1D1D1F]">{disc.field}:</span>
                        {comparison.confidence === 0 ? (
                          <span data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1552:26" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1552" data-component-end-line="1552" data-component-file="MeasurementVerificationStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-red-600%20font-medium%22%7D" className="text-sm text-red-600 font-medium">{disc.difference}</span>
                        ) : (
                          <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1554:26" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1554" data-component-end-line="1561" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%22%7D" className="flex items-center gap-3">
                            <span data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1555:28" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1555" data-component-end-line="1555" data-component-file="MeasurementVerificationStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22You%3A%22%7D" className="text-sm text-[#86868B]">You: {disc.userValue}</span>
                            <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1556:28" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1556" data-component-end-line="1556" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowRightIcon%22%2C%22className%22%3A%22text-%5B%2386868B%5D%22%7D" name="ArrowRightIcon" size={16} className="text-[#86868B]" />
                            <span data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1557:28" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1557" data-component-end-line="1557" data-component-file="MeasurementVerificationStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-amber-600%20font-medium%22%2C%22textContent%22%3A%22AI%3A%22%7D" className="text-sm text-amber-600 font-medium">AI: {disc.aiValue}</span>
                            <span data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1558:28" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1558" data-component-end-line="1560" data-component-file="MeasurementVerificationStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-amber-600%20bg-amber-100%20px-2%20py-1%20rounded%22%2C%22textContent%22%3A%22(%20diff)%22%7D" className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded">
                              ({disc.difference} diff)
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {comparison.matches && (
                <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1570:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1570" data-component-end-line="1574" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-4%20pt-4%20border-t%20border-green-200%22%7D" className="mt-4 pt-4 border-t border-green-200">
                  <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1571:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1571" data-component-end-line="1573" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-green-700%20font-medium%22%2C%22textContent%22%3A%22%E2%9C%93%20All%20measurements%20match%20AI%20analysis%22%7D" className="text-sm text-green-700 font-medium">
                    ✓ All measurements match AI analysis
                  </p>
                </div>
              )}
            </motion.div>
          ))}

          {/* Show individual image errors if any */}
          {comparisons.filter(c => c.imageIndex >= 0 && c.confidence === 0).map((comparison, index) => (
            <motion.div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1581:12" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1581" data-component-end-line="1603" data-component-file="MeasurementVerificationStep.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22p-6%20rounded-2xl%20border-2%20bg-red-50%20border-red-200%22%7D"
              key={`error-${comparison.imageIndex}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className="p-6 rounded-2xl border-2 bg-red-50 border-red-200"
            >
              <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1588:14" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1588" data-component-end-line="1602" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-4%22%7D" className="flex items-start gap-4">
                <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1589:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1589" data-component-end-line="1591" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-3%20rounded-xl%20bg-red-100%22%7D" className="p-3 rounded-xl bg-red-100">
                  <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1590:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1590" data-component-end-line="1590" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XCircleIcon%22%2C%22className%22%3A%22text-red-600%22%7D" name="XCircleIcon" size={32} className="text-red-600" />
                </div>
                <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1592:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1592" data-component-end-line="1601" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
                  <h3 data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1593:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1593" data-component-end-line="1595" data-component-file="MeasurementVerificationStep.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-%5B%231D1D1F%5D%20mb-1%22%2C%22textContent%22%3A%22Image%20-%20Analysis%20Failed%22%7D" className="text-lg font-semibold text-[#1D1D1F] mb-1">
                    Image {comparison.imageIndex + 1} - Analysis Failed
                  </h3>
                  {comparison.discrepancies.length > 0 && (
                    <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1597:20" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1597" data-component-end-line="1599" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-red-600%20font-medium%20mt-2%22%7D" className="text-sm text-red-600 font-medium mt-2">
                      {comparison.discrepancies[0].difference}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}

          {allVerified ? (
            aiSourceOfTruth ? null : (
              <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1608:14" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1608" data-component-end-line="1618" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%20bg-gradient-to-br%20from-green-500%20to-green-600%20rounded-2xl%20text-white%22%7D" className="p-6 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl text-white">
                <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1609:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1609" data-component-end-line="1617" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-4%22%7D" className="flex items-center gap-4">
                  <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1610:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1610" data-component-end-line="1610" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%7D" name="CheckCircleIcon" size={48} />
                  <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1611:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1611" data-component-end-line="1616" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                    <h3 data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1612:20" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1612" data-component-end-line="1612" data-component-file="MeasurementVerificationStep.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-semibold%20mb-1%22%2C%22textContent%22%3A%22All%20Measurements%20Verified!%22%7D" className="text-xl font-semibold mb-1">All Measurements Verified!</h3>
                    <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1613:20" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1613" data-component-end-line="1615" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20opacity-90%22%2C%22textContent%22%3A%22Your%20measurements%20match%20the%20AI%20analysis.%20You%20can%20proceed%20with%20confidence.%22%7D" className="text-sm opacity-90">
                      Your measurements match the AI analysis. You can proceed with confidence.
                    </p>
                  </div>
                </div>
              </div>
            )
          ) : serviceMismatch ? null : (
            <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1621:12" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1621" data-component-end-line="1634" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%20bg-amber-50%20border-2%20border-amber-200%20rounded-2xl%22%7D" className="p-6 bg-amber-50 border-2 border-amber-200 rounded-2xl">
              <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1622:14" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1622" data-component-end-line="1633" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-4%22%7D" className="flex items-start gap-4">
                <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1623:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1623" data-component-end-line="1623" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ExclamationTriangleIcon%22%2C%22className%22%3A%22text-amber-600%20flex-shrink-0%20mt-1%22%7D" name="ExclamationTriangleIcon" size={32} className="text-amber-600 flex-shrink-0 mt-1" />
                <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1624:16" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1624" data-component-end-line="1632" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                  <h3 data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1625:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1625" data-component-end-line="1627" data-component-file="MeasurementVerificationStep.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-%5B%231D1D1F%5D%20mb-2%22%2C%22textContent%22%3A%22Some%20Discrepancies%20Found%22%7D" className="text-lg font-semibold text-[#1D1D1F] mb-2">
                    Some Discrepancies Found
                  </h3>
                  <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1628:18" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1628" data-component-end-line="1631" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-%5B%2386868B%5D%20mb-4%22%2C%22textContent%22%3A%22The%20AI%20detected%20some%20differences%20between%20your%20measurements%20and%20the%20images.%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20Please%20review%20the%20discrepancies%20above.%20You%20can%20still%20proceed%20if%20your%20measurements%20are%20correct.%22%7D" className="text-sm text-[#86868B] mb-4">
                    The AI detected some differences between your measurements and the images. 
                    Please review the discrepancies above. You can still proceed if your measurements are correct.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : currentImages.length === 0 && !verifying ? (
        <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1638:8" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1638" data-component-end-line="1646" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-12%20bg-white%20border-2%20border-gray-200%20rounded-2xl%20text-center%22%7D" className="p-12 bg-white border-2 border-gray-200 rounded-2xl text-center">
          <Icon data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1639:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1639" data-component-end-line="1639" data-component-file="MeasurementVerificationStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CameraIcon%22%2C%22className%22%3A%22mx-auto%20mb-4%20text-%5B%2386868B%5D%22%7D" name="CameraIcon" size={64} className="mx-auto mb-4 text-[#86868B]" />
          <h3 data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1640:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1640" data-component-end-line="1640" data-component-file="MeasurementVerificationStep.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-semibold%20text-%5B%231D1D1F%5D%20mb-2%22%2C%22textContent%22%3A%22No%20Images%20Uploaded%22%7D" className="text-xl font-semibold text-[#1D1D1F] mb-2">No Images Uploaded</h3>
          <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1641:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1641" data-component-end-line="1645" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B%2386868B%5D%20mb-6%22%7D" className="text-[#86868B] mb-6">
            {aiSourceOfTruth
              ? 'Upload a photo above so AI can measure your item and calculate the price.'
              : 'Upload photos above to verify your measurements with AI, or skip verification to proceed.'}
          </p>
        </div>
      ) : null}

      {/* Action Buttons.
          AI source-of-truth mode has NO inline Continue: the analysis
          auto-confirms (see verifyMeasurements) and unlocks the page's own
          "Continue" button, so a second button here would be redundant. We
          only show a short instruction while it's still locked. Manual /
          verify mode keeps its Skip + Continue pair. */}
      {!verifying && (
        aiSourceOfTruth ? (
          !allVerified && (
            <p data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1658:12" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1658" data-component-end-line="1661" data-component-file="MeasurementVerificationStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22pt-6%20text-center%20text-sm%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22Upload%20a%20photo%20of%20your%20and%20tap%20%E2%80%9CVerify%20Measurements%20with%20AI%E2%80%9D.%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20Once%20it%E2%80%99s%20analysed%2C%20press%20to%20confirm%20and%20proceed.%22%7D" className="pt-6 text-center text-sm text-[#86868B]">
              Upload a photo of your {serviceName.toLowerCase()} and tap “Verify Measurements with AI”.
              Once it’s analysed, press <span data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1660:40" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1660" data-component-end-line="1660" data-component-file="MeasurementVerificationStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%22%2C%22textContent%22%3A%22Continue%22%7D" className="font-semibold">Continue</span> to confirm and proceed.
            </p>
          )
        ) : (
          <div data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1664:10" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1664" data-component-end-line="1682" data-component-file="MeasurementVerificationStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-4%20pt-6%22%7D" className="flex gap-4 pt-6">
            <button data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1665:12" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1665" data-component-end-line="1670" data-component-file="MeasurementVerificationStep.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex-1%20px-6%20py-3.5%20rounded-full%20border-2%20border-gray-200%20bg-white%20text-%5B%231D1D1F%5D%20font-semibold%20hover%3Aborder-%5B%23204653%5D%20hover%3Abg-%5B%23E4EDF0%5D%20transition-all%20duration-200%22%2C%22textContent%22%3A%22Skip%20Verification%22%7D"
              onClick={handleSkip}
              className="flex-1 px-6 py-3.5 rounded-full border-2 border-gray-200 bg-white text-[#1D1D1F] font-semibold hover:border-[#204653] hover:bg-[#E4EDF0] transition-all duration-200"
            >
              Skip Verification
            </button>
            <button data-component-id="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx:1671:12" data-component-path="src/app/service-booking-flow/components/MeasurementVerificationStep.tsx" data-component-line="1671" data-component-end-line="1681" data-component-file="MeasurementVerificationStep.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              onClick={handleContinue}
              disabled={verificationResults.length === 0 && currentImages.length > 0}
              className={`flex-1 px-6 py-3.5 rounded-full font-semibold transition-all duration-200 ${
                allVerified || verificationResults.length === 0 || currentImages.length === 0
                  ? 'bg-[#204653] text-white hover:bg-[#1a3a44] shadow-[0_4px_20px_rgb(74,124,126,0.3)]'
                  : 'bg-amber-500 text-white hover:bg-amber-600'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {allVerified || verificationResults.length === 0 || currentImages.length === 0 ? 'Continue' : 'Continue Anyway'}
            </button>
          </div>
        )
      )}
    </motion.div>
  );
};

export default MeasurementVerificationStep;
